import React from "react";
import { useAppSelector, useAppDispatch } from "../../../../app/hooks";
import {
  createDVIRReport,
  toggleShowAddReportModal,
  getDVIRTemplates,
} from "../../dvirSlice";
import { getDriversV2 } from "../../../DriversV2/driversV2Slice";
import { getVehicles } from "../../../Vehicles/vehiclesSlice";
import "./AddReportModal.scss";

export function AddReportModal() {
  const dispatch = useAppDispatch();

  const showModal = useAppSelector((state) => state.dvir.showAddReportModal);
  const createAPI = useAppSelector((state) => state.dvir.createDVIRReport);
  const templates = useAppSelector((state) => state.dvir.templates);
  const drivers = useAppSelector((state) => state.driversV2.drivers);
  const vehicles = useAppSelector((state) => state.vehicles.vehicles);

  const [driverId, setDriverId] = React.useState("");
  const [vehicleId, setVehicleId] = React.useState("");
  const [templateId, setTemplateId] = React.useState("");
  const [date, setDate] = React.useState(() => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  });

  React.useEffect(() => {
    if (showModal) {
      // Load data when modal opens
      if (templates.length === 0) {
        dispatch(getDVIRTemplates());
      }
      if (drivers.length === 0) {
        dispatch(getDriversV2());
      }
      if (vehicles.length === 0) {
        dispatch(getVehicles());
      }
    }
  }, [showModal, dispatch, templates.length, drivers.length, vehicles.length]);

  const handleClose = () => {
    dispatch(toggleShowAddReportModal());
    // Reset form
    setDriverId("");
    setVehicleId("");
    setTemplateId("");
    setDate(new Date().toISOString().split("T")[0]);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!driverId || !vehicleId || !templateId || !date) return;

    dispatch(
      createDVIRReport({
        driverId,
        vehicleId,
        templateId,
        date,
      })
    );
  };

  const isFormValid = driverId && vehicleId && templateId && date;

  if (!showModal) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="add-report-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Create DVIR Report</h3>
          <button className="close-button" onClick={handleClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label htmlFor="date">Date</label>
              <input
                type="date"
                id="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="form-input"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="driver">Driver</label>
              <select
                id="driver"
                value={driverId}
                onChange={(e) => setDriverId(e.target.value)}
                className="form-input"
                required
              >
                <option value="">Select a driver</option>
                {drivers.map((driver) => (
                  <option key={driver.id} value={driver.userId}>
                    {driver.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="vehicle">Vehicle</label>
              <select
                id="vehicle"
                value={vehicleId}
                onChange={(e) => setVehicleId(e.target.value)}
                className="form-input"
                required
              >
                <option value="">Select a vehicle</option>
                {vehicles.map((vehicle) => (
                  <option key={vehicle.id} value={vehicle.id}>
                    {vehicle.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="template">Template</label>
              <select
                id="template"
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
                className="form-input"
                required
              >
                <option value="">Select a template</option>
                {templates
                  .filter((t) => t.isActive)
                  .map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleClose}
              disabled={createAPI.loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={createAPI.loading || !isFormValid}
            >
              {createAPI.loading ? "Creating..." : "Create Report"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
